import React, { Fragment, useMemo } from 'react';
import { DOCUMENT_OPTIONS, STEPS } from '../pages/RfiCreate/utils';
import StepFieldWrapper from '../pages/RfiCreate/components/StepFieldWrapper';
import { useTranslation } from 'react-i18next';
import {
  DATA_ROOM_RFI_FILE_TYPES,
  DATA_ROOM_RFI_NOTIFICATION_MEDIUM,
  DataRoomRFIFilePeriod,
  DataRoomRfiTemplate,
} from 'router/subrouters/DataRoom/models/DataRoomRfi';

type RfiDocumentOption = {
  name: string;
  period: DataRoomRFIFilePeriod;
  checked?: boolean;
};

const getDocuments = () =>
  DOCUMENT_OPTIONS.map(({ value }) => ({
    name: value,
    period: undefined,
    checked: false,
  }));

const getDocumentsForInitialValues = (
  checkedDocuments?: RfiDocumentOption[],
) => {
  const documents = getDocuments();

  if (!checkedDocuments.length)
    return { documents, customDocuments: [] as RfiDocumentOption[] };

  const checkedDoc: RfiDocumentOption[] = [];
  const checkedCustomDoc: RfiDocumentOption[] = [];

  checkedDocuments.forEach((el) => {
    if (Object.values(DATA_ROOM_RFI_FILE_TYPES).includes(el.name)) {
      checkedDoc.push(el);
    } else {
      checkedCustomDoc.push(el);
    }
  });

  const initialDocuments = documents.map(({ name }) => {
    const inChecked = checkedDoc.find((d) => d.name === name);
    return {
      name,
      period: inChecked?.period || undefined,
      checked: !!inChecked,
    };
  });

  return {
    documents: initialDocuments,
    customDocuments: checkedCustomDoc.map((el) => ({ ...el, checked: true })),
  };
};

export default function useCreateRfi(
  isCreatingOrEditingTemplate: boolean,
  template?: DataRoomRfiTemplate,
) {
  const { t } = useTranslation();

  const initialValues = useMemo(
    () => ({
      ...getDocumentsForInitialValues(template?.documents || []),
      ...(!isCreatingOrEditingTemplate && {
        recipients: [],
        twentyFourHoursBeforeDeadlineReminder: true,
        atDeadlineReminder: true,
      }),
      ...(template && {
        customNote: template.customNote,
        notificationMedium: {
          [DATA_ROOM_RFI_NOTIFICATION_MEDIUM.EMAIL]:
            template.notificationMedium.includes(
              DATA_ROOM_RFI_NOTIFICATION_MEDIUM.EMAIL,
            ),
          [DATA_ROOM_RFI_NOTIFICATION_MEDIUM.SMS]:
            template.notificationMedium.includes(
              DATA_ROOM_RFI_NOTIFICATION_MEDIUM.SMS,
            ),
          [DATA_ROOM_RFI_NOTIFICATION_MEDIUM.PLATFORM]:
            template.notificationMedium.includes(
              DATA_ROOM_RFI_NOTIFICATION_MEDIUM.PLATFORM,
            ),
        },
      }),
      ...(template &&
        isCreatingOrEditingTemplate && { saveAsTemplateName: template.name }),
    }),
    [isCreatingOrEditingTemplate, template],
  );

  const RenderSteps = useMemo(
    () =>
      STEPS.map(({ Step, title }, index) => (
        <Fragment key={index}>
          {index > 0 && <hr />}
          <StepFieldWrapper stepNumber={index + 1} title={t(title)}>
            <Step isCreatingOrEditingTemplate={isCreatingOrEditingTemplate} />
          </StepFieldWrapper>
        </Fragment>
      )),
    [isCreatingOrEditingTemplate, t],
  );

  return { initialValues, RenderSteps };
}
